import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function IvIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':     return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'search':   return <svg {...p}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
        case 'person':   return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'plus':     return <svg {...p}><path d="M12 5v14M5 12h14"/></svg>;
        case 'check':    return <svg {...p}><path d="M20 6 9 17l-5-5"/></svg>;
        case 'star':     return <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
        case 'shield':   return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'x':        return <svg {...p}><path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg>;
        default: return null;
    }
}

export default function Invitar() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [profileInfo, setProfileInfo] = useState({ id: 0 });
    const [gangName, setGangName]       = useState('');
    const [allUsers, setAllUsers]       = useState([]);
    const [requests, setRequests]       = useState({ invitations: [], demands: [], winegangUsers: [] });
    const [searchText, setSearchText]   = useState('');
    const [invited, setInvited]         = useState({});
    const [ready, setReady]             = useState(false);
    const [fetchError, setFetchError]   = useState(false);
    const [retryKey, setRetryKey]       = useState(0);

    useEffect(() => {
        const stored = localStorage.getItem('userLogged');
        if (stored) setProfileInfo(JSON.parse(stored));
    }, []);

    useEffect(() => {
        if (!profileInfo.id) return;
        setFetchError(false);

        const xhrGang = new XMLHttpRequest();
        xhrGang.open('GET', `${process.env.REACT_APP_API_URL}/winegang/${id}`, true);
        xhrGang.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    const data = JSON.parse(this.responseText);
                    if (Number(profileInfo.id) !== Number(data.admin_user_id)) { navigate('/'); return; }
                    setGangName(data.winegang?.winegang_name || '');
                } else {
                    setFetchError(true);
                }
            }
        };
        xhrGang.send();

        const xhrUsers = new XMLHttpRequest();
        xhrUsers.open('GET', `${process.env.REACT_APP_API_URL}/users`, true);
        xhrUsers.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setAllUsers(JSON.parse(this.responseText));
                requestAnimationFrame(() => setReady(true));
            }
        };
        xhrUsers.send();

        const xhrReq = new XMLHttpRequest();
        xhrReq.open('GET', `${process.env.REACT_APP_API_URL}/findByWinegangId/${id}`, true);
        xhrReq.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setRequests(JSON.parse(this.responseText));
            }
        };
        xhrReq.send();
    }, [profileInfo.id, retryKey]);

    const sendInvite = (userId) => {
        if (invited[userId]) return;
        setInvited(prev => ({ ...prev, [userId]: 'loading' }));

        const data = new FormData();
        data.append('user_id', userId);
        data.append('winegang_id', id);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/invitation`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 201) {
                setInvited(prev => ({ ...prev, [userId]: 'done' }));
                setRequests(prev => ({
                    ...prev,
                    invitations: [...prev.invitations, { user_id: userId }],
                }));
            }
        };
        xhr.send(data);
    };

    const filteredUsers = allUsers.filter(user => {
        const uid = Number(user.id);
        const isAssociated = requests.winegangUsers?.some(wu => Number(wu.user_id) === uid);
        const isInvited    = requests.invitations?.some(inv => Number(inv.user_id) === uid);
        const hasDemand    = requests.demands?.some(d => Number(d.user_id) === uid);
        return !isAssociated && !isInvited && !hasDemand &&
            user.username.toLowerCase().includes(searchText.toLowerCase());
    });

    return (
        <div className={`iv-stage${ready ? ' iv-ready' : ''}`}>
            <div className="iv-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="iv-vignette" />
            <div className="iv-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            <div className="iv-header">
                <button className="gn-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <IvIcon name="back" size={18} />
                </button>
                <div className="iv-header-center">
                    <IvIcon name="shield" size={15} />
                    <span className="iv-header-title">{gangName || t('gangs.search_player')}</span>
                </div>
                <div style={{ width: 40 }} />
            </div>

            {/* Search */}
            <div className="iv-search-wrap">
                <div className="iv-search-field">
                    <IvIcon name="search" size={16} />
                    <input
                        className="iv-search-input"
                        type="text"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder={t('gangs.placeholder_search')}
                        autoComplete="off"
                    />
                    {searchText && (
                        <button className="iv-search-clear" onClick={() => setSearchText('')} aria-label={t('common.clear')}>
                            <IvIcon name="x" size={14} />
                        </button>
                    )}
                </div>
                <p className="iv-result-count">
                    {filteredUsers.length} {t('gangs.player', { count: filteredUsers.length })}
                </p>
            </div>

            {/* User list */}
            <div className="iv-scroll">
                {filteredUsers.length > 0 ? (
                    <div className="iv-list">
                        {filteredUsers.map((user, i) => {
                            const isDone = invited[user.id] === 'done';
                            const isLoading = invited[user.id] === 'loading';
                            const gang = user.relevant_winegang?.name || '';
                            return (
                                <div
                                    key={user.id}
                                    className="iv-card"
                                    style={{ animationDelay: `${Math.min(i, 12) * 0.04}s` }}
                                >
                                    <button className="iv-card-name-area" onClick={() => navigate(`/user/${user.id}`)}>
                                        <div className="iv-card-icon">
                                            <IvIcon name="person" size={16} />
                                        </div>
                                        <div className="iv-card-info">
                                            <span className="iv-username">{user.username}</span>
                                            <div className="iv-meta">
                                                <IvIcon name="star" size={10} />
                                                <span>{user.average.toFixed(1)}%</span>
                                                {gang && <><IvIcon name="shield" size={10} /><span>{gang}</span></>}
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        className={`iv-invite-btn${isDone ? ' iv-invite-done' : ''}`}
                                        onClick={() => sendInvite(user.id)}
                                        disabled={isDone || isLoading}
                                        aria-label={t('gangs.col_invite')}
                                    >
                                        {isLoading ? <span className="inv-spinner" /> :
                                         isDone    ? <IvIcon name="check" size={14} /> :
                                                     <IvIcon name="plus"  size={14} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : ready ? (
                    <div className="inv-empty">
                        <IvIcon name="search" size={34} />
                        <p>{searchText ? t('gangs.invite_no_results') : t('gangs.invite_no_players')}</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
